'use client';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import queryString from 'query-string';

import { ENV } from '@shared/config';
import type { Questions } from '@shared/types';

import { QuestionsQueryKey } from './keys';

// Интерфейс для ответа с сервера
interface ServerQuestionsResponse {
  result_code: number;
  data: Questions;
  lang: string;
  userID: string | null;
  pager: {
    page_current: number;
    pages_all: number;
    records_all: number;
    records_per_page: number;
  };
}

// Интерфейс для обработанного ответа
export interface QuestionsResponse {
  data: Questions;
  pager: {
    page_current: number;
    pages_all: number;
    records_all: number;
    records_per_page: number;
  };
}

export function useGetQuestionsQuery({
  noteId,
  currency,
  page = 1,
  pagelen = 5,
  enabled = true,
}: {
  noteId: string;
  currency: string;
  page?: number;
  pagelen?: number;
  enabled?: boolean;
}) {
  return useQuery(
    [QuestionsQueryKey, noteId, currency, page, pagelen],
    async (): Promise<QuestionsResponse> => {
      try {
        const requestLink = `${ENV.API}/question_list/${noteId}/?${queryString.stringify({
          currency,
          lang: ENV.LANG,
          page,
          pagelen,
        })}`;

        const { data: rawResponse } = await axios.get<ServerQuestionsResponse>(requestLink, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        // Преобразуем серверный ответ в нужный формат
        return {
          data: rawResponse.data,
          pager: rawResponse.pager,
        };
      } catch (error) {
        console.error('Ошибка получения вопросов о товаре:', {
          noteId,
          currency,
          page,
          pagelen,
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          url: `${ENV.API}/question_list/${noteId}/?${queryString.stringify({
            currency,
            lang: ENV.LANG,
            page,
            pagelen,
          })}`,
        });
        
        // Отправляем ошибку в систему логирования
        if (typeof window !== 'undefined') {
          try {
            void fetch('/api-v2/send-log', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                level: 3,
                msg: JSON.stringify({
                  error: 'Ошибка загрузки вопросов о товаре (пагинация)',
                  noteId,
                  currency,
                  page,
                  pagelen,
                  details: error instanceof Error ? error.message : String(error),
                }),
                href: window.location.href,
                titlePage: document?.title,
                userAgent: navigator?.userAgent,
              }),
            });
          } catch (logError) {
            console.error('Ошибка отправки лога:', logError);
          }
        }
        
        // Возвращаем пустой ответ с базовой пагинацией при ошибке
        return {
          data: [],
          pager: {
            page_current: page,
            pages_all: 0,
            records_all: 0,
            records_per_page: pagelen,
          },
        };
      }
    },
    {
      enabled: enabled && !!noteId && !!currency,
      staleTime: 2 * 60 * 1000, // 2 минуты для пагинированных данных
      cacheTime: 5 * 60 * 1000, // 5 минут
      retry: 1, // Повторить запрос один раз при ошибке
      retryDelay: 1000, // Задержка 1 секунда перед повтором
      keepPreviousData: true, // Сохранять предыдущие данные при загрузке новой страницы
    },
  );
} 